import filterFactory from './filter-factory.js';
import {Product} from '../../@types/wishlist.js';
import {trackFilterUsage} from '../service/cube-tracking-layer.js';
import {Filter} from './filter.js';
import {dispatchEmperaFilterChangedEvent} from './empera-filter-event.js';
import {FilterRepository, FilterRepositoryImpl, StoredActiveOption} from './filter-repository.js';
import {FilterCountCache} from './filter-count-cache.js';
import {ScoredFilterOption} from './option-ui-model.js';

export class FilterMenu {
  public static filterRepository: FilterRepository = new FilterRepositoryImpl(); // NOSONAR Should be mutable for jest tests, but not for production code.

  private static readonly countCache: FilterCountCache = new FilterCountCache(); // Cache for filter counts

  readonly isOpen: boolean;

  readonly filter: Filter[];

  readonly resultCount: number;

  readonly isScoringEnabled: boolean;

  readonly scoredOptions: ScoredFilterOption[];

  private constructor(
    filter: Filter[],
    resultCount: number,
    isOpen: boolean,
    isScoringEnabled: boolean = true,
    scoredOptions: ScoredFilterOption[] = [],
  ) {
    this.filter = filter;
    this.resultCount = resultCount;
    this.isOpen = isOpen;
    this.isScoringEnabled = isScoringEnabled;
    this.scoredOptions = scoredOptions;
    this.dispatchFilterChangedEvent();
  }

  public isEdited() {
    return this.filter.some(f => f.isEdited());
  }

  public open(products: Product[]) {
    return FilterMenu.calculateCounts(products, this.filter, false, true, this.isScoringEnabled); // update counts when opening filter menu
  }

  public close() {
    return new FilterMenu(this.filter, this.resultCount, false, this.isScoringEnabled, this.scoredOptions);
  }

  public undoEditState(products: Product[]) {
    this.filter.forEach(f => f.undoEditState());
    return FilterMenu.calculateCounts(products, this.filter, false, this.isOpen, this.isScoringEnabled);
  }

  public applyEditState(products?: Product[]) {
    if (!this.isEdited()) {
      return this;
    }
    const trackEmptyFilter = this.hasActiveOptionsBeforeEdit(false);
    const trackEmptySort = this.hasActiveOptionsBeforeEdit(true);
    this.filter.forEach(f => f.applyEditState());
    this.storeActiveOptions();
    this.trackUsage(trackEmptyFilter, trackEmptySort);
    if (products) {
      return FilterMenu.calculateCounts(products, this.filter, false, this.isOpen, this.isScoringEnabled);
    }
    return new FilterMenu(this.filter, this.resultCount, this.isOpen, this.isScoringEnabled, this.scoredOptions);
  }

  private hasActiveOptionsBeforeEdit(isSort: boolean) {
    return this.filter
      .filter(f => f.isSort === isSort)
      .some(f => f.hasActiveOptionsBeforeEdit());
  }

  private trackUsage(trackEmptyFilter: boolean, trackEmptySort: boolean) {
    this.trackUsageForSortOrFilter(false, trackEmptyFilter);
    this.trackUsageForSortOrFilter(true, trackEmptySort);
  }

  private trackUsageForSortOrFilter(isSort: boolean, trackEmpty: boolean) {
    const trackingOptions = this.getTrackingValues(isSort);
    if (trackEmpty || trackingOptions.length > 0) {
      trackFilterUsage(isSort, isSort ? this.getTrackingValues(isSort, true) : trackingOptions);
    }
  }

  private getTrackingValues(isSort: boolean, includeDefault: boolean = false) {
    return this.filter
      .filter(f => f.isSort === isSort)
      .map(f => f.getTrackingOptions(includeDefault))
      .filter(trackingValue => trackingValue != null)
      .map(trackingValue => trackingValue);
  }

  private storeActiveOptions() {
    FilterMenu.filterRepository.storeActiveOptions(this.filter
      .flatMap(f => f.getAppliedActiveAndNotDefaultOptions())
      .map(option => new StoredActiveOption(option.id)));
  }

  public resetAllFilters(products: Product[], filterConfig = filterFactory(products)) {
    const cleanInstance = FilterMenu.createMenuFor(products, true, filterConfig, this.isOpen, this.isScoringEnabled);
    cleanInstance.storeActiveOptions();
    return cleanInstance;
  }

  public reapplyFilter(products: Product[]) {
    return FilterMenu.calculateCounts(products, this.filter, false, this.isOpen, this.isScoringEnabled);
  }

  public resetFilter(products: Product[], filterId: string) {
    this.filter.forEach(f => f.id === filterId && f.reset());
    return FilterMenu.calculateCounts(products, this.filter, true, this.isOpen, this.isScoringEnabled, this.scoredOptions);
  }

  public toggleFilterOption(products: Product[], filterOptionId: string, allowRemove: boolean) {
    this.filter.forEach(f => f.toggleFilterOption(filterOptionId, allowRemove));
    return FilterMenu.calculateCounts(products, this.filter, true, this.isOpen, this.isScoringEnabled, this.scoredOptions);
  }

  public toggleAndApplyFilterOption(products: Product[], filterOptionId: string, allowRemove: boolean) {
    this.filter.forEach(f => f.toggleFilterOption(filterOptionId, allowRemove));
    if (!this.isEdited()) {
      return FilterMenu.calculateCounts(products, this.filter, false, this.isOpen, this.isScoringEnabled);
    }
    const trackEmptyFilter = this.hasActiveOptionsBeforeEdit(false);
    const trackEmptySort = this.hasActiveOptionsBeforeEdit(true);
    this.filter.forEach(f => f.applyEditState());
    this.storeActiveOptions();
    this.trackUsage(trackEmptyFilter, trackEmptySort);
    return FilterMenu.calculateCounts(products, this.filter, false, this.isOpen, this.isScoringEnabled);
  }

  public getStaticOneClickOptions() {
    return this.filter.flatMap(f => f.getOptions().filter(o => o.isOneClick));
  }

  public getRecommendedOneClickOptions(limit: number) {
    if (!this.isScoringEnabled) {
      return [];
    }
    const staticOneClickOptions = this.getStaticOneClickOptions();
    return [...this.scoredOptions]
      .sort((a, b) => b.score - a.score)
      .filter(r => staticOneClickOptions.every(o => o.id !== r.option.id))
      .slice(0, limit);
  }

  public filterAndSortProducts(products: Product[], includeEditState: boolean = false) {
    if (products.length === 0) {
      return products;
    }
    const filteredProducts = FilterMenu.filterProductsForActiveOptions(products, this.filter, includeEditState);
    const sort = this.filter.filter(f => f.isSort);
    if (sort.length === 0) {
      return products;
    }
    return sort[0].sort(filteredProducts);
  }

  public getAppliedActiveAndNotDefaultOptionsCount(excludeOnlySort: boolean = false) {
    const isFromInterest = (f: Filter) => !excludeOnlySort || !f.isSort;
    return this.filter
      .filter(f => isFromInterest(f))
      .reduce((sum, current) => sum + current.getAppliedActiveAndNotDefaultOptions().length, 0);
  }

  public static createMenuFor(
    products: Product[],
    ignoreActiveOptionsFromUrl: boolean = false,
    filterConfig = filterFactory(products),
    isOpen: boolean = false,
    isScoringEnabled: boolean = true,
  ) {
    const filter = filterConfig.filter;
    this.initializeAllOptions(filter, ignoreActiveOptionsFromUrl);
    const filterMenu = FilterMenu.calculateCounts(products, filter, false, isOpen, isScoringEnabled);
    filterMenu.trackUsage(ignoreActiveOptionsFromUrl, false);
    return filterMenu;
  }

  private static initializeAllOptions(allFilters: Filter[], ignoreActiveOptionsFromUrl: boolean) {
    const activeOptionIds = ignoreActiveOptionsFromUrl ? [] : FilterMenu.filterRepository.getActiveOptions();
    if (activeOptionIds.length > 0) {
      allFilters.forEach((f) => {
        activeOptionIds.forEach(activeOption => f.toggleFilterOption(activeOption.id, true));
        if (!ignoreActiveOptionsFromUrl) {
          f.applyEditState();
        }
      });
    }
  }

  private static calculateCounts(
    products: Product[],
    filter: Filter[],
    includeEditState: boolean,
    isOpen: boolean,
    isScoringEnabled: boolean = true,
    previousScoredOptions: ScoredFilterOption[] = [],
  ) {
    const filteredProducts = FilterMenu.filterProductsForActiveOptions(products, filter, includeEditState);
    const shouldUpdateScoring = isScoringEnabled && !includeEditState;
    filter.forEach(f => f.resetCount());
    filter.forEach(f => f.countProducts(
      filteredProducts,
      products,
      filter,
      includeEditState,
      shouldUpdateScoring,
      FilterMenu.countCache
    ));
    const scoredOptions = shouldUpdateScoring
      ? filter.flatMap(f => f.getScoredOptions(filteredProducts.length))
      : previousScoredOptions;
    return new FilterMenu(filter, filteredProducts.length, isOpen, isScoringEnabled, scoredOptions);
  }

  public static invalidateCache(): void {
    FilterMenu.countCache.invalidate();
  }

  private static filterProductsForActiveOptions(
    products: Product[],
    filter: Filter[],
    includeEditState: boolean = false,
  ) {
    return products.filter(product => filter.every(group => group.applyFilter(product, includeEditState)));
  }

  private dispatchFilterChangedEvent() {
    const activeFilter = this.filter
      .flatMap(f => f.getAppliedActiveAndNotDefaultOptions())
      .map(option => option.id);
    const reductionFilter = ['on-sale', 'in-promotion'].some(f => activeFilter.includes(f));
    const isLowStockFilter = activeFilter.includes('is-low-stock');
    dispatchEmperaFilterChangedEvent({reductionFilter, isLowStockFilter});
  }
}
